import { useRef, useState } from 'react';
import { ticketTypeGlyphLabel } from '@vd/annotation-core';
import { Button, Popover } from '@vd/ui';
import { MarkerPicker } from './MarkerPicker';
import { TypeMarker } from './TypeMarker';

/**
 * An existing type's marker, shown as the marker and changed from the grid in a
 * popover (TOADO-526). A grid in every row of the types list would bury the list,
 * so the row shows the one it has.
 *
 * The arrow keys move a choice inside the popover without saving it; a click on a
 * marker, or Enter or Space, saves it and closes. Escape or a click outside leaves
 * the marker as it was.
 */

/** Eight 40px markers a row, so the 24 make three full rows; fewer on a narrow window. */
const POPOVER_WIDTH = 380;
const VIEWPORT_MARGIN = 16;
const MIN_WIDTH = 120;
export function MarkerButton({ glyph, typeName, onChange, disabled = false }: {
  glyph: string;
  typeName: string;
  onChange: (glyph: string) => void;
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [width, setWidth] = useState(POPOVER_WIDTH);
  const [pending, setPending] = useState(glyph);
  const button = useRef<HTMLButtonElement>(null);
  const label = ticketTypeGlyphLabel(glyph);

  const close = () => {
    setAnchor(null);
    button.current?.focus();
  };

  return (
    <>
      <Button
        ref={button}
        variant="secondary"
        size="sm"
        disabled={disabled}
        leftIcon={<TypeMarker glyph={glyph} height={22} />}
        aria-label={`Marker for ${typeName}: ${label}`}
        aria-haspopup="dialog"
        aria-expanded={anchor !== null}
        onClick={(event) => {
          if (anchor) {
            setAnchor(null);
            return;
          }
          setPending(glyph);
          // Measured as it opens, against the window it opens in; never narrower
          // than one row of markers.
          setWidth(Math.max(MIN_WIDTH, Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN)));
          setAnchor(event.currentTarget.getBoundingClientRect());
        }}
      >
        {label}
      </Button>
      <Popover
        open={anchor !== null}
        anchor={anchor}
        onClose={close}
        width={width}
        estimatedHeight={190}
      >
        <div style={{ padding: 12 }}>
          <MarkerPicker
            value={pending}
            label={`Marker for ${typeName}`}
            autoFocus
            onChange={setPending}
            onCommit={(next) => {
              close();
              if (next !== glyph) onChange(next);
            }}
          />
        </div>
      </Popover>
    </>
  );
}
